import { Check, Minus } from 'lucide-react'

export interface ComparisonRow {
  readonly feature: string
  /** Cell can be a checkmark (true), a dash (false), or free text. */
  readonly mindweb: string | boolean
  readonly other: string | boolean
}

interface ComparisonTableProps {
  readonly mindwebLabel: string
  readonly otherLabel: string
  readonly rows: readonly ComparisonRow[]
}

function Cell({ value, accent }: { readonly value: string | boolean; readonly accent?: boolean }) {
  if (value === true) {
    return <Check className={accent ? 'h-5 w-5 text-primary' : 'h-5 w-5 text-muted-foreground'} aria-label="Yes" />
  }
  if (value === false) {
    return <Minus className="h-5 w-5 text-muted-foreground/50" aria-label="No" />
  }
  return <span className="text-sm leading-relaxed text-muted-foreground">{value}</span>
}

/** Side-by-side feature comparison used by the /compare/* pages. */
export function ComparisonTable({ mindwebLabel, otherLabel, rows }: ComparisonTableProps) {
  return (
    <div className="glass-card overflow-hidden rounded-2xl">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-[color-mix(in_oklch,var(--foreground)_10%,transparent)]">
            <td className="px-5 py-4">
              <span className="sr-only">Feature</span>
            </td>
            <th className="px-5 py-4 text-sm font-semibold text-foreground" scope="col">
              {mindwebLabel}
            </th>
            <th className="px-5 py-4 text-sm font-semibold text-muted-foreground" scope="col">
              {otherLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.feature}
              className="border-b border-[color-mix(in_oklch,var(--foreground)_6%,transparent)] last:border-0"
            >
              <th
                scope="row"
                className="px-5 py-4 align-top text-sm font-medium text-foreground"
              >
                {row.feature}
              </th>
              <td className="px-5 py-4 align-top">
                <Cell value={row.mindweb} accent />
              </td>
              <td className="px-5 py-4 align-top">
                <Cell value={row.other} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
